import React, { useEffect, useMemo, useRef } from 'react'
import { useQuill } from 'react-quilljs'
import 'quill/dist/quill.snow.css'
import BlotFormatter from 'quill-blot-formatter'
import { useField } from 'formik'
import Quill from 'quill'
import { fileUpload } from './fileUtils'
import { toast } from 'react-toastify'

// Register BlotFormatter module for image resizing
if (!Quill.imports['modules/blotFormatter']) {
  Quill.register('modules/blotFormatter', BlotFormatter)
}

interface PlaceholderItem {
  id: string
  label: string
}

export interface CFieldProps {
  name: string
  editorProps?: {
    placeholderList?: PlaceholderItem[]
  }
}

export const RenderContent = ({ content }: { content: string }) => {
  let html = ''
  try {
    const delta = JSON.parse(content || '{}')
    const temp = document.createElement('div')
    const quill = new Quill(temp)
    quill.setContents(delta)
    html = temp.children[0].innerHTML // This becomes your HTML
  } catch (error) {
    html = content
  }

  return (
    <div
      style={{ whiteSpace: 'pre-wrap' }}
      dangerouslySetInnerHTML={{ __html: html }}
    ></div>
  )
}

// Helper: convert a video URL to an embeddable URL
const getEmbedUrl = (url: string): { type: 'iframe' | 'video' | null; src: string } => {
  // YouTube: youtube.com/watch?v=ID or youtu.be/ID
  const ytMatch = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/
  )
  if (ytMatch) {
    return { type: 'iframe', src: `https://www.youtube.com/embed/${ytMatch[1]}` }
  }

  // Vimeo: vimeo.com/ID
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/)
  if (vimeoMatch) {
    return { type: 'iframe', src: `https://player.vimeo.com/video/${vimeoMatch[1]}` }
  }

  // Direct video file
  if (/\.(mp4|webm|ogg|mov)(\?|$)/i.test(url)) {
    return { type: 'video', src: url }
  }

  return { type: null, src: '' }
}

export default function Editor(props: CFieldProps) {
  const placeholderList: PlaceholderItem[] = useMemo(
    () => props.editorProps?.placeholderList || [],
    [props.editorProps]
  )

  // Store cursor position before file dialog steals focus
  const savedRangeRef = useRef<{ index: number; length: number }>({ index: 0, length: 0 })

  // Build toolbar modules configuration
  const toolbarOptions = useMemo(() => {
    const baseToolbar = [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ font: [] }],
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      ['link', 'image', 'video'],
      [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ direction: 'rtl' }],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      ['clean']
    ]

    // Add placeholder dropdown if placeholders exist
    if (placeholderList.length > 0) {
      baseToolbar.push([
        { placeholder: placeholderList.map((p) => p.id) } as any
      ])
    }

    return baseToolbar
  }, [placeholderList])

  const { quill, quillRef } = useQuill({
    modules: {
      toolbar: toolbarOptions,
      blotFormatter: {}
    }
  })
  const [field, , helpers] = useField(props.name)

  // Generate CSS for placeholder dropdown labels, editor height, and media
  useEffect(() => {
    const styleId = 'quill-custom-styles'
    let styleTag = document.getElementById(styleId)

    if (!styleTag) {
      styleTag = document.createElement('style')
      styleTag.id = styleId
      document.head.appendChild(styleTag)
    }

    const placeholderCss = placeholderList
      .map(
        (p) => `
        .ql-picker.ql-placeholder .ql-picker-item[data-value="${p.id}"]::before {
          content: "${p.label}";
        }
      `
      )
      .join('\n')

    styleTag.textContent =
      placeholderCss +
      `
        .ql-picker.ql-placeholder {
          width: 130px;
        }
        .ql-picker.ql-placeholder > span.ql-picker-label::before {
          content: 'Merge Fields';
        }
        .ql-picker.ql-placeholder .ql-picker-options {
          max-height: 200px;
          overflow-y: auto;
        }
        .ql-container.ql-snow {
          height: 400px;
          font-size: 16px;
          border: none !important;
        }
        .ql-editor {
          height: 100%;
          min-height: 100%;
          overflow-y: auto;
        }
        .ql-editor img {
          max-width: 100%;
          height: auto;
          display: inline-block;
          margin: 10px 0;
          cursor: pointer;
        }
        .ql-editor iframe {
          width: 200px;
          height: 200px;
          border-radius: 8px;
          margin: 12px 0;
          border: none;
        }
        .ql-editor video {
          width: 200px;
          height: 200px;
          object-fit: cover;
          border-radius: 8px;
          margin: 12px 0;
        }
        .ql-editor a {
          color: #008080 !important;
          text-decoration: underline !important;
        }
        .ql-tooltip {
          z-index: 10000;
        }
      `

    return () => {
      const tag = document.getElementById(styleId)
      if (tag) tag.remove()
    }
  }, [placeholderList])

  // Insert Image at a specific position (uses saved range, not live selection)
  const insertToEditor = (url: string) => {
    if (!quill) {
      console.error('[Editor] quill instance is null at insertToEditor')
      return
    }
    const range = savedRangeRef.current
    console.log('[Editor] insertToEditor called — url:', url, 'range:', range)
    console.log('[Editor] quill.getLength() before insert:', quill.getLength())

    quill.focus()
    quill.insertEmbed(range.index, 'image', url)
    quill.setSelection(range.index + 1)

    console.log('[Editor] quill.getLength() after insert:', quill.getLength())
  }

  // Upload Image to Cloudinary and insert into editor
  const saveToServer = async (file: File, previewNode?: HTMLImageElement) => {
    console.log('[Editor] saveToServer (Cloudinary) called — file:', file.name)
    const toastId = toast.loading('Finalizing upload...')
    try {
      const fileName = await fileUpload(
        file,
        window.location.pathname.split('/')[1]
      )
      console.log('[Editor] fileUpload returned:', fileName)

      if (fileName) {
        if (previewNode) {
          previewNode.src = fileName
          previewNode.style.opacity = '1'
        } else {
          insertToEditor(fileName)
        }
        toast.update(toastId, {
          render: 'Image uploaded!',
          type: 'success',
          isLoading: false,
          autoClose: 3000
        })
      } else {
        if (previewNode) previewNode.remove()
        toast.update(toastId, {
          render: 'Upload failed',
          type: 'error',
          isLoading: false,
          autoClose: 3000
        })
      }
    } catch (error) {
      if (previewNode) previewNode.remove()
      toast.update(toastId, {
        render: 'Failed to upload image',
        type: 'error',
        isLoading: false,
        autoClose: 3000
      })
    }
  }

  // Open Dialog to select Image File
  const selectLocalImage = () => {
    if (!quill) return

    const input = document.createElement('input')
    input.setAttribute('type', 'file')
    input.setAttribute('accept', 'image/*')
    input.click()

    input.onchange = () => {
      const file = input.files?.[0]
      if (!file) return

      // Save cursor position
      const currentRange = quill.getSelection()
      const range = currentRange
        ? { index: currentRange.index, length: currentRange.length }
        : { index: quill.getLength(), length: 0 }
      savedRangeRef.current = range

      // Immediate preview using Base64
      const reader = new FileReader()
      reader.onload = (e) => {
        const base64 = e.target?.result as string
        if (!base64) return

        quill.focus()
        quill.insertEmbed(range.index, 'image', base64)
        quill.setSelection(range.index + 1)

        // Find the newly inserted image node to replace later
        // We use a specific selector to find the one we just inserted
        const images = quill.root.querySelectorAll('img')
        const previewNode = Array.from(images).find(img => img.src === base64)

        if (previewNode) {
          previewNode.style.opacity = '0.5' // Show it's uploading
          saveToServer(file, previewNode as HTMLImageElement)
        } else {
          saveToServer(file) // Fallback if node not found
        }
      }
      reader.readAsDataURL(file)
    }
  }

  const [modal, setModal] = React.useState<'link' | 'video' | null>(null)
  const [modalUrl, setModalUrl] = React.useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (modal && inputRef.current) inputRef.current.focus()
  }, [modal])

  const openModal = (type: 'link' | 'video') => {
    if (!quill) return
    const range = quill.getSelection()
    savedRangeRef.current = range
      ? { index: range.index, length: range.length }
      : { index: quill.getLength(), length: 0 }
    setModal(type)
    setModalUrl('')
  }

  const closeModal = () => {
    setModal(null)
    setModalUrl('')
  }

  const handleModalInsert = () => {
    if (!quill || !modal) return

    if (!modalUrl.trim()) {
      toast.error(`Please enter a ${modal === 'link' ? 'link' : 'video'} URL`)
      return
    }

    const range = savedRangeRef.current
    quill.focus()

    if (modal === 'link') {
      try {
        if (range.length > 0) {
          quill.formatText(range.index, range.length, 'link', modalUrl)
        } else {
          quill.insertText(range.index, modalUrl, 'link', modalUrl)
          quill.setSelection(range.index + modalUrl.length)
        }
        toast.success('Link inserted successfully')
      } catch (err) {
        toast.error('Failed to insert link')
      }
    } else if (modal === 'video') {
      const trimmedUrl = modalUrl.trim()
      const embed = getEmbedUrl(trimmedUrl)

      if (!embed.type) {
        toast.error('Please enter a valid YouTube, Vimeo, or direct video URL')
        return
      }

      try {
        if (embed.type === 'iframe') {
          quill.insertEmbed(range.index, 'video', embed.src)
          quill.setSelection(range.index + 1)
        } else {
          const videoHtml =
            `<video controls width="200" height="200" style="width:200px;height:200px;object-fit:cover;border-radius:8px;margin:12px 0">` +
            `<source src="${embed.src}" type="video/mp4">` +
            `</video>`
          quill.clipboard.dangerouslyPasteHTML(range.index, videoHtml)
        }
        toast.success('Video embedded successfully')
      } catch (err) {
        toast.error('Failed to embed video')
      }
    }

    closeModal()
  }

  React.useEffect(() => {
    if (quill) {
      if (field.value && quill.root.innerHTML !== field.value) {
        quill.clipboard.dangerouslyPasteHTML(field.value)
      }
      quill.on('text-change', () => {
        helpers.setValue(quill.root.innerHTML)
      })
      const toolbar = quill.getModule('toolbar') as any
      toolbar.addHandler('image', selectLocalImage)
      toolbar.addHandler('video', () => openModal('video'))
      toolbar.addHandler('link', () => openModal('link'))
    }
    // eslint-disable-next-line
  }, [quill])

  // Set up placeholder dropdown click handlers
  useEffect(() => {
    if (!quill || placeholderList.length === 0) return

    const toolbar = quill.getModule('toolbar') as any
    const toolbarElement = toolbar?.container as HTMLElement
    if (!toolbarElement) return

    const pickerItems = toolbarElement.querySelectorAll(
      '.ql-placeholder .ql-picker-item'
    )

    const handleClick = (e: Event) => {
      const target = e.target as HTMLElement
      const value = target.getAttribute('data-value')
      if (!value) return

      quill.focus()
      const range = quill.getSelection(true)
      const placeholderText = `{{${value}}}`
      quill.insertText(range.index, placeholderText)
      quill.setSelection(range.index + placeholderText.length)
      
      toast.info(`Merge field "${value}" inserted`, { autoClose: 2000 })

      const pickerLabel = toolbarElement.querySelector(
        '.ql-placeholder .ql-picker-label'
      )
      if (pickerLabel) {
        pickerLabel.setAttribute('data-value', '')
      }
    }

    pickerItems.forEach((item) => {
      item.addEventListener('click', handleClick)
    })

    return () => {
      pickerItems.forEach((item) => {
        item.removeEventListener('click', handleClick)
      })
    }
  }, [quill, placeholderList])

  return (
    <div style={{ minHeight: 400, backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', position: 'relative' }}>
      <div ref={quillRef} />

      {/* URL Input Modal */}
      {modal && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40' onClick={closeModal}>
          <div
            className='bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6'
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className='text-lg font-semibold text-gray-800 mb-4'>
              {modal === 'link' ? 'Insert Link' : 'Insert YouTube/Vimeo URL'}
            </h3>
            <input
              ref={inputRef}
              type='text'
              className='w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#008080]/30 focus:border-[#008080]'
              placeholder={
                modal === 'link'
                  ? 'https://example.com'
                  : 'https://youtube.com/watch?v=...'
              }
              value={modalUrl}
              onChange={(e) => setModalUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleModalInsert()
                if (e.key === 'Escape') closeModal()
              }}
            />
            <div className='flex justify-end gap-3 mt-5'>
              <button
                type='button'
                className='px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors'
                onClick={closeModal}
              >
                Cancel
              </button>
              <button
                type='button'
                className='px-4 py-2 text-sm font-medium text-white bg-[#008080] rounded-lg hover:bg-[#007373] transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                disabled={!modalUrl.trim()}
                onClick={handleModalInsert}
              >
                Insert
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
